// Suggested creator rates by market. The index is 2024 GDP per capita at PPP
// relative to the United States, from the World Bank's WDI indicator
// NY.GDP.PCAP.PP.CD. PPP adjusts the commercial benchmark; the current FX
// table then converts that value from the benchmark currency into the market's
// own currency. Liechtenstein has no observation and uses Switzerland as a
// transparent proxy. Suggestions stay editable.

import {crossRate, roundAmountUp} from './fx.ts';
import {convertCents, MARKET_CURRENCIES, marketCurrencyForCountry} from './money.ts';
import type {RatesPerEur} from './money.ts';
import type {MarketRate} from '../shared/api.ts';

export const MARKET_RATE_INDEX_YEAR = 2024;
export const MARKET_RATE_SOURCE = 'World Bank GDP per capita, PPP';

const US_INDEX: Readonly<Record<string, number>> = Object.freeze({
  AT: .8794, AU: .8369, BE: .8614, BG: .4871, BR: .2596, CA: .7599,
  CH: 1.1311, CY: .7312, CZ: .6687, DE: .8583, DK: .9393, EE: .5848,
  ES: .6738, FI: .7559, FR: .7256, GB: .7293, GR: .5126, HR: .575,
  HU: .5633, IE: 1.6337, IL: .6647, IS: .9697, IT: .7229, JP: .6285,
  KR: .7085, LI: 1.1311, LT: .6464, LU: 1.8113, LV: .5092, MT: .8131,
  MX: .2996, NL: .989, NO: 1.2262, NZ: .6475, PL: .5949, PT: .6094,
  RO: .5695, RS: .381, SE: .8265, SG: 1.7774, SI: .6681, SK: .5553,
  TR: .5237, UA: .2163, US: 1, ZA: .1796,
});

export interface MarketPrice {
  amount_cents: number;
  currency: string;
}

/** A market's GDP/PPP ratio to the US; one for an unknown country. */
export function marketRateIndex(country: string): number {
  return US_INDEX[String(country || '').trim().toUpperCase()] ?? 1;
}

/**
 * One explicit, local-currency rate for every selected non-US market. The US
 * remains the benchmark rather than a duplicate override. FX rates are units
 * per EUR, so target/source is the cross-rate from the benchmark to local.
 */
export function suggestMarketRates(
  countries: readonly string[],
  benchmarkAmountCents: number,
  benchmarkCurrency: string,
  perEur: RatesPerEur,
): MarketRate[] {
  if (!Number.isFinite(benchmarkAmountCents) || benchmarkAmountCents <= 0) return [];
  return [...new Set(countries.map((country) => String(country).trim().toUpperCase()))]
    .filter((country) => country && country !== 'US')
    .map((country) => {
      const currency = marketCurrencyForCountry(country);
      const rate = crossRate(perEur, benchmarkCurrency, currency);
      const local = rate
        ? benchmarkAmountCents * marketRateIndex(country) * rate
        : convertCents(benchmarkAmountCents * marketRateIndex(country), benchmarkCurrency, currency, perEur);
      return {country, amount_cents: roundAmountUp(local, currency), currency};
    });
}

/** Strict enough for stored JSON from either editor; malformed entries disappear. */
export function parseMarketRates(value: unknown, legacyCurrency = 'USD'): MarketRate[] {
  let source = value;
  if (typeof source === 'string') {
    try { source = JSON.parse(source); } catch { return []; }
  }
  if (!Array.isArray(source)) return [];
  const rates = new Map<string, MarketRate>();
  for (const raw of source) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as Partial<MarketRate>;
    const country = String(entry.country || '').trim().toUpperCase();
    const amount = Number(entry.amount_cents);
    const storedCurrency = String(entry.currency || '').trim().toUpperCase();
    // Rows written before local-market pricing had no currency and were in the
    // job's currency. Callers pass that as legacyCurrency so they remain true.
    const currency = storedCurrency || legacyCurrency;
    if (!/^[A-Z]{2}$/.test(country) || country === 'US' || !Number.isInteger(amount) || amount <= 0
      || !MARKET_CURRENCIES.includes(currency)) continue;
    rates.set(country, {country, amount_cents: amount, currency});
  }
  return [...rates.values()];
}

/** The actual source price for a creator market, with its currency. */
export function amountForMarket(
  baseAmountCents: number,
  baseCurrency: string,
  rates: readonly MarketRate[],
  country: string,
): MarketPrice {
  const code = String(country || '').trim().toUpperCase();
  const rate = rates.find((entry) => entry.country === code);
  return rate ? {amount_cents: rate.amount_cents, currency: rate.currency} : {amount_cents: baseAmountCents, currency: baseCurrency};
}

/** Lowest and highest selected-market offers expressed in one target currency. */
export function marketRateRange(
  baseAmountCents: number,
  baseCurrency: string,
  rates: readonly MarketRate[],
  countries: readonly string[],
  perEur: RatesPerEur,
  targetCurrency = baseCurrency,
): {low: number; high: number; currency: string} {
  const prices = countries.length
    ? countries.map((country) => amountForMarket(baseAmountCents, baseCurrency, rates, country))
    : [{amount_cents: baseAmountCents, currency: baseCurrency}];
  const amounts = prices.map((price) => convertCents(price.amount_cents, price.currency, targetCurrency, perEur));
  return {low: Math.min(...amounts), high: Math.max(...amounts), currency: targetCurrency};
}
